import React, { Fragment, useEffect, useState } from "react";
import { CardBody, Col, Row, Table } from "reactstrap";
import Select from "react-select";
import { Link } from "react-router-dom";
import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { getPaginationRange } from "services/utils";
import { rowsPerPageOptions } from "./InsuranceHelper";

interface CourseContainerProps {
  columns: any[];
  data: any[];
  customPageSize?: number;
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  divClass?: string;
  pagination?: {
    page: number;
    total_items: number;
    total_pages: number;
    per_page: number;
  };
  onPageChange?: (page: number) => void;
  limit: number;
  setLimit?: React.Dispatch<React.SetStateAction<number>>;
  setPage: (page: number) => void;
  setRowId: React.Dispatch<React.SetStateAction<string | null>>;
  onRowClick?: (row: any) => void;
}

const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => setValue(initialValue), [initialValue]);
  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);
    return () => clearTimeout(timeout);
  }, [value, onChange, debounce]);

  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} className="form-control search" />;
};

const CourseContainer = ({
  columns,
  data,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  divClass,
  pagination,
  onPageChange,
  limit,
  setLimit,
  setPage,
  onRowClick,
}: CourseContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: { fuzzy: fuzzyFilter },
    state: { columnFilters, globalFilter },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageSize } = table;

  useEffect(() => {
    if (customPageSize) {
      setPageSize(customPageSize);
    }
  }, [customPageSize, setPageSize]);

  return (
    <Fragment>
      <CardBody className={divClass}>
        <div className="table-responsive table-card">
          <Table hover className={tableClass}>
            <thead
              className={theadClass ?? "bg-primary text-white"}
              style={{ fontSize: "0.875rem" }}
            >
              {getHeaderGroups().map((headerGroup) => (
                <tr className={trClass ?? ""} key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{ cursor: "pointer", padding: "12px" }}
                    >
                      {header.isPlaceholder ? null : (
                        <>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanFilter()}
                        </>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {getRowModel().rows.map((row, rowIndex) => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row)}
                  style={{
                    ...(rowIndex % 2 === 1 && { backgroundColor: "#73C3A91F" }),
                    cursor: "pointer",
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} style={{ verticalAlign: "middle", whiteSpace: "normal" }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </CardBody>

      {/* PAGINATION */}
      {pagination && (
        <Row className="align-items-center mt-2 g-3">
          <div className="col-sm text-muted">
            Showing{" "}
            <span className="fw-semibold ms-1">
              {Math.min(pagination.per_page * pagination.page, pagination.total_items)}
            </span>{" "}
            of <span className="fw-semibold">{pagination.total_items}</span> Results
          </div>

          <div className="col-sm-auto">
            <ul className="pagination pagination-separated pagination-md mb-0">
              <li className={`page-item ${pagination.page <= 1 ? "disabled" : ""}`}>
                <Link to="#" className="page-link" onClick={() => onPageChange?.(pagination.page - 1)}>
                  Previous
                </Link>
              </li>

              {getPaginationRange(pagination.page, pagination.total_pages).map((p, idx) => (
                <li key={idx} className={`page-item ${p === pagination.page ? "active" : ""}`}>
                  <Link
                    to="#"
                    className="page-link"
                    onClick={() => onPageChange?.(p as number)}
                  >
                    {p}
                  </Link>
                </li>
              ))}

              <li className={`page-item ${pagination.page >= pagination.total_pages ? "disabled" : ""}`}>
                <Link className="page-link" to="#"  onClick={() => onPageChange?.(pagination.page + 1)}>
                  Next
                </Link>
              </li>
            </ul>
          </div>

          {/* <div className="col-sm-auto">
            { <Select
              value={rowsPerPageOptions.find((opt) => opt.value === limit)}
              onChange={(selected: any) => {
                setPage(1);
                setLimit?.(selected?.value || 10);
              }}
              options={rowsPerPageOptions}
              isSearchable={false}
              menuPlacement="top"
            /> }
          </div> */}
        </Row>
      )}
    </Fragment>
  );
};

export default CourseContainer;
